import { makeStyles } from '@material-ui/core';

// Vocabulary matched to the rest of Project Development (GanttChart/Styles.js,
// ProjectAnalytics/Styles.js): #173072 brand navy, #ec6535 brand orange,
// neutral greys for borders and secondary text.
const BORDER = '#E4E7EC';
const HEADING = '#101828';
const MUTED = '#667085';

// Every campaign table shares one column template so the columns line up down
// the whole page instead of each table sizing itself to its own longest stage
// name — the single biggest thing that made the first cut look unfinished.
// Stage gives up 6% to the four count columns: at a 1rem bold heading
// 'Completed Batches' is 149px wide and 16.5% could not hold it, while the
// longest stage label measured (232px) still clears 28%.
export const STAGE_COL_WIDTHS = ['28%', '18%', '18%', '18%', '18%'];

export const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3, 4),
    fontFamily: theme.typography.fontFamily,
    color: HEADING,
  },

  // ── screen header ─────────────────────────────────────────────────────────
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: theme.spacing(2), marginBottom: theme.spacing(2),
  },
  // Back sits to the LEFT of the title, before it, the way Gantt Chart and
  // Batch Analytics already do it. This screen had it alone on the right.
  headerLeft: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(2), minWidth: 0,
  },
  title: { fontSize: '1.25rem', fontWeight: 600, color: '#173072', whiteSpace: 'nowrap' },
  backButton: {
    display: 'flex', alignItems: 'center', cursor: 'pointer', color: HEADING,
    '&:hover': { opacity: 0.75 },
  },

  // ── filter bar ────────────────────────────────────────────────────────────
  filterRow: {
    display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap',
    gap: theme.spacing(2.5), paddingBottom: theme.spacing(2.5),
    borderBottom: `1px solid ${BORDER}`, marginBottom: theme.spacing(2.5),
  },
  // Filter labels sit at the same bold 1rem as the column headings below them,
  // so the top of the screen does not read a size smaller than its tables.
  fieldLabel: {
    fontSize: '1rem', fontWeight: 'bold', color: '#344054', marginBottom: 6,
  },
  field: { minWidth: 230 },
  // The collapsed dropdown shows the placeholder in grey until something is
  // checked, matching the "Select CMO…" state on the empty screen.
  placeholder: { color: '#98A2B3' },
  filterActions: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) },
  searchButton: {
    backgroundColor: '#ec6535', color: '#fff', textTransform: 'none',
    borderRadius: '2rem', minWidth: 116, height: 38, padding: '6px 22px',
    fontSize: '0.875rem', fontWeight: 600, boxShadow: 'none',
    '&:hover': { backgroundColor: '#000', color: '#fff' },
    '&.Mui-disabled': { backgroundColor: '#EAECF0', color: '#98A2B3' },
  },
  reportButton: {
    backgroundImage: 'linear-gradient(to right, #301232, #1A2D6C, #173072)',
    color: '#fff', textTransform: 'none', borderRadius: '2rem',
    minWidth: 158, height: 38, padding: '6px 22px',
    fontSize: '0.875rem', fontWeight: 600, boxShadow: 'none',
    '&:hover': { backgroundImage: 'none', backgroundColor: '#000' },
    '&.Mui-disabled': { backgroundImage: 'none', backgroundColor: '#EAECF0', color: '#98A2B3' },
  },

  // ── results pane ──────────────────────────────────────────────────────────
  // FSD §5: the results scroll vertically once they outgrow the visible area,
  // with the filter bar and Last Synced staying put above them. Bounded by the
  // viewport rather than a fixed pixel height so the pane grows on a large
  // screen instead of scrolling a short list for no reason; the minimum keeps
  // it usable on a laptop.
  resultsPane: {
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 'calc(100vh - 320px)',
    minHeight: 220,
    paddingRight: theme.spacing(0.5),
  },

  // ── last synced / refresh ─────────────────────────────────────────────────
  // One per screen (FSD §5), sitting above the section list.
  syncRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    gap: theme.spacing(0.5), marginBottom: theme.spacing(1),
  },
  syncText: { fontSize: '0.75rem', color: MUTED, whiteSpace: 'nowrap' },
  refreshButton: {
    textTransform: 'none', fontSize: '0.75rem', fontStyle: 'italic',
    color: '#ec6535', minWidth: 0, padding: '2px 8px',
  },

  // ── collapsible sections ──────────────────────────────────────────────────
  // ── summary tiles (FSD v3 section 5) ──────────────────────────────────────
  tileRow: { display: 'flex', gap: theme.spacing(2), margin: theme.spacing(0, 0, 2) },
  tile: {
    flex: 1, display: 'flex', background: '#fff', borderRadius: 6,
    border: '1px solid #E4E7EC', overflow: 'hidden', minWidth: 0,
  },
  // The coloured accent is a bar down the leading edge, as the slide draws it.
  tileAccent: { width: 4, flex: '0 0 4px' },
  tileBody: { padding: theme.spacing(1.5, 2), minWidth: 0 },
  tileCount: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.1 },
  tileLabel: { fontSize: '0.8125rem', color: '#667085', marginTop: 2, whiteSpace: 'nowrap' },

  tipLine: { fontSize: '0.8125rem', color: '#667085', margin: theme.spacing(0, 0, 1.5) },

  // Status dot and count badge on a section heading.
  sectionDot: { width: 8, height: 8, borderRadius: '50%', flex: '0 0 8px', marginRight: 8 },
  sectionBadge: {
    marginLeft: 10, padding: '1px 8px', borderRadius: 10, background: '#F2F4F7',
    color: '#475467', fontSize: '0.75rem', fontWeight: 600,
  },

  // Completion bar beneath the percentage. The track is always drawn, so a
  // stage with no plan reads as an empty bar rather than a missing one.
  progressTrack: {
    marginTop: 4, height: 4, borderRadius: 2, background: '#EAECF0',
    width: '100%', maxWidth: 120, overflow: 'hidden',
  },
  progressFill: { display: 'block', height: '100%', background: '#2E9E4F' },

  // "Synthetic Step A" under the stage name.
  stageSub: { fontSize: '0.75rem', color: '#667085', marginTop: 2 },

  section: { marginBottom: theme.spacing(4) },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1),
    cursor: 'pointer', background: 'none', border: 'none', padding: 0,
    font: 'inherit', color: HEADING, textAlign: 'left',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: { fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '0.01em' },
  sectionBody: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },

  // ── one campaign card ─────────────────────────────────────────────────────
  // The card is what separates one campaign from the next; without it the
  // stacked meta rows and tables ran together down the page.
  campaignCard: {
    display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1),
    border: `1px solid ${BORDER}`, borderRadius: 8, background: '#fff',
    padding: theme.spacing(2, 2.5), maxWidth: 1180,
  },
  campaignChevron: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, flexShrink: 0, marginTop: -2,
    background: 'none', border: 'none', borderRadius: 4, padding: 0,
    cursor: 'pointer', color: '#475467',
    '&:hover': { background: '#F2F4F7' },
  },
  campaignBody: { flex: 1, minWidth: 0 },

  // Five labelled facts laid on an auto-fitting grid so a long CMO name gets a
  // column of its own instead of crowding the ones beside it.
  campaignMeta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: theme.spacing(1.5, 3),
  },
  // These five are the card's own column headings, so they match the stage
  // table's: bold 1rem, and Title Case rather than tracked-out capitals, which
  // is how column names are written everywhere else ("Site / Plant").
  // Widest is "Campaign Status" at 131px, inside a 170px grid track.
  metaLabel: {
    fontSize: '1rem', fontWeight: 'bold', color: MUTED, marginBottom: 2,
  },
  metaValue: { fontSize: '0.875rem', fontWeight: 500, color: HEADING, lineHeight: 1.4 },
  statusPill: {
    display: 'inline-block', fontSize: '0.875rem', fontWeight: 600,
    borderRadius: 12, padding: '2px 10px', lineHeight: 1.5,
  },
  statusOngoing: { background: '#FFF4E5', color: '#B54708' },
  statusCompleted: { background: '#E7F6EC', color: '#067647' },

  // ── stage table ───────────────────────────────────────────────────────────
  tableWrap: {
    marginTop: theme.spacing(2), border: `1px solid ${BORDER}`, borderRadius: 6,
    overflowX: 'auto',
  },
  // Widened with the heading size: at 1rem bold the longest heading
  // ('Completed Batches') measures 149px and needs 181px with its padding, so
  // at 18% per count column the table cannot go below ~1006px without a
  // heading spilling into the column beside it. Under that the wrapper scrolls
  // horizontally, which is readable; overlapping text is not.
  table: { tableLayout: 'fixed', minWidth: 1010 },
  // Column headings are bold 1rem, matching the table headings elsewhere in
  // the application. Body cells stay at 0.875rem so the heading still reads as
  // a heading.
  headCell: {
    fontSize: '1rem', fontWeight: 'bold', color: '#344054',
    background: '#F9FAFB', borderBottom: `1px solid ${BORDER}`,
    padding: '11px 16px', whiteSpace: 'nowrap',
  },
  bodyCell: {
    fontSize: '0.875rem', color: '#344054',
    borderBottom: '1px solid #F2F4F7', padding: '13px 16px',
  },
  // Last row sits on the wrapper's own border; a second line looks like a gap.
  lastRowCell: { borderBottom: 'none' },
  stageCell: { fontWeight: 500, color: HEADING },
  completionCell: { fontWeight: 600, color: HEADING },
  // A dash is absence of data, not a value — it should recede, not read as 0.
  dashCell: { color: '#98A2B3' },

  // ── notes / empty states ──────────────────────────────────────────────────
  cardNote: {
    fontSize: '0.75rem', color: MUTED, marginTop: theme.spacing(1.25),
    fontStyle: 'italic',
  },
  centered: { display: 'flex', justifyContent: 'center', padding: theme.spacing(8, 0) },
  emptyPane: {
    border: `1px dashed ${BORDER}`, borderRadius: 8, background: '#FCFCFD',
    padding: theme.spacing(5, 3), textAlign: 'center', maxWidth: 1180,
  },
  emptyText: { fontSize: '0.875rem', color: MUTED },
}));
